import { useState } from "react";
import axios from "axios";
import { Folder, FileText, Upload, Trash2, Loader2, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import KbUploadDialog from "@/components/KbUploadDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ACCESS_BADGES = {
  everyone: { label: "Everyone", class: "bg-green-100 text-green-700" },
  usergroup_a: { label: "Group A", class: "bg-blue-100 text-blue-700" },
  usergroup_b: { label: "Group B", class: "bg-amber-100 text-amber-700" },
  usergroup_c: { label: "Group C", class: "bg-purple-100 text-purple-700" },
};

export default function FolderView({ subHub, folders, icon: HubIcon, color }) {
  const [documents, setDocuments] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const fetchDocs = async (folder) => {
    setLoading(true);
    setSelectedFolder(folder);
    try {
      const res = await axios.get(`${API}/knowledge/documents`, {
        params: { sub_hub: subHub, folder },
        headers: getHeaders(),
      });
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
      setDocuments([]);
    }
    setLoading(false);
  };

  const handleDelete = async (docId) => {
    setDeleting(docId);
    try {
      await axios.delete(`${API}/knowledge/documents/${docId}`, {
        headers: getHeaders(),
      });
      toast.success("Document deleted");
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch {
      toast.error("Failed to delete document");
    }
    setDeleting(null);
  };

  const handleUploaded = () => {
    setUploadOpen(false);
    if (selectedFolder) fetchDocs(selectedFolder);
  };

  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    purple: "from-purple-500 to-purple-600",
    green: "from-green-500 to-emerald-600",
    amber: "from-amber-500 to-orange-600",
    indigo: "from-indigo-500 to-indigo-600",
    orange: "from-orange-500 to-red-600",
    cyan: "from-cyan-500 to-teal-500",
  };

  const folderColors = {
    blue: "border-blue-200 hover:border-blue-400",
    purple: "border-purple-200 hover:border-purple-400",
    green: "border-green-200 hover:border-green-400",
    amber: "border-amber-200 hover:border-amber-400",
    indigo: "border-indigo-200 hover:border-indigo-400",
    orange: "border-orange-200 hover:border-orange-400",
    cyan: "border-cyan-200 hover:border-cyan-400",
  };

  const iconColors = {
    blue: "text-blue-500 bg-blue-50",
    purple: "text-purple-500 bg-purple-50",
    green: "text-emerald-500 bg-emerald-50",
    amber: "text-amber-500 bg-amber-50",
    indigo: "text-indigo-500 bg-indigo-50",
    orange: "text-orange-500 bg-orange-50",
    cyan: "text-cyan-500 bg-cyan-50",
  };

  const gradient = colorClasses[color] || colorClasses.blue;
  const borderColor = folderColors[color] || folderColors.blue;
  const iconColor = iconColors[color] || iconColors.blue;

  // Folder grid view
  if (!selectedFolder) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-600">
            {folders.length} folder{folders.length !== 1 ? "s" : ""}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {folders.map((folder) => (
            <div
              key={folder}
              onClick={() => fetchDocs(folder)}
              className={`bg-white border-2 ${borderColor} p-6 cursor-pointer transition-all hover:shadow-md`}
            >
              <div className={`w-14 h-14 mx-auto mb-4 flex items-center justify-center ${iconColor}`}>
                <Folder className="w-7 h-7" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-lg font-semibold text-slate-800 text-center">
                {folder}
              </h3>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Document list view
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="text-slate-600 hover:text-slate-700 p-0"
            onClick={() => { setSelectedFolder(null); setDocuments([]); }}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-semibold text-slate-800">
            {selectedFolder}
          </h2>
        </div>

        <Button
          onClick={() => setUploadOpen(true)}
          className={`bg-gradient-to-r ${gradient} rounded-none text-white`}
          size="sm"
        >
          <Upload className="w-4 h-4 mr-1" /> Upload
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: color === "blue" ? "#3b82f6" : "#10b981" }} />
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <Folder className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No documents in this folder yet.</p>
          <Button
            onClick={() => setUploadOpen(true)}
            className={`bg-gradient-to-r ${gradient} rounded-none text-white`}
          >
            <Upload className="w-4 h-4 mr-2" /> Upload First Document
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const badge = ACCESS_BADGES[doc.access_level] || ACCESS_BADGES.everyone;
            return (
              <div key={doc.id} className="bg-white border border-slate-200 p-4 flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">
                      {doc.document_name || doc.title}
                    </p>
                    <p className="text-xs text-slate-400 font-mono mt-0.5">{doc.unique_id}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`text-xs font-normal ${badge.class}`}>
                        {badge.label}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        {doc.file_type?.toUpperCase()} • {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0 ml-2"
                  onClick={() => handleDelete(doc.id)}
                  disabled={deleting === doc.id}
                >
                  {deleting === doc.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <KbUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        subHub={subHub}
        folders={folders}
        selectedFolder={selectedFolder}
        onSuccess={handleUploaded}
      />
    </div>
  );
}
